<template>
  <section class="encounter-message-thread">
    <header class="encounter-message-head">
      <button type="button" class="encounter-message-toggle" :aria-expanded="isExpanded"
        :aria-controls="panelId" @click="toggleExpanded">
        <div class="encounter-message-title">
          <span class="encounter-message-icon"><i class="material-icons">forum</i></span>
          <div>
            <span>Messages</span>
            <small>Encounter-specific authorization notes</small>
          </div>
        </div>
        <span class="encounter-message-controls">
          <span class="encounter-message-count">{{ messages.length }}</span>
          <i class="material-icons encounter-message-chevron"
            :title="isExpanded ? 'Collapse messages' : 'Expand messages'">
            {{ isExpanded ? "expand_less" : "expand_more" }}
          </i>
        </span>
      </button>
    </header>

    <Transition name="encounter-message-panel">
      <div class="encounter-message-body" :class="{ 'has-body-close': showBodyClose }"
        :id="panelId" v-show="isExpanded">
        <div class="progress encounter-message-progress" v-if="isLoading || isSending">
          <div class="indeterminate"></div>
        </div>

        <button type="button" class="encounter-message-body-close" title="Close messages"
          aria-label="Close messages" @click="toggleExpanded" v-if="showBodyClose">
          <i class="material-icons">close</i>
        </button>

        <div class="encounter-message-error" v-if="error">
          <i class="material-icons">error_outline</i>
          <span>{{ error }}</span>
        </div>

        <div class="encounter-message-list" ref="messageList" v-if="messages.length">
          <article class="encounter-message-row"
            :class="{ 'is-mine': isOwnMessage(message), 'is-new': isNewMessage(message) }"
            v-for="message in messages" :key="message.id">
            <div class="encounter-message-meta">
              <strong>{{ messageSender(message) }}</strong>
              <span class="encounter-message-time">
                <span class="encounter-message-new" v-if="isNewMessage(message)">New</span>
                <time>{{ formatDate(message.created_at) }}</time>
              </span>
            </div>
            <p>{{ message.body }}</p>
          </article>
        </div>

        <div class="encounter-message-empty" v-else-if="!isLoading">
          <i class="material-icons">chat_bubble_outline</i>
          <span>{{ emptyMessage }}</span>
        </div>

        <div class="encounter-message-composer" v-if="!readOnly && (draftMode || canPost)">
          <label :for="composerId">{{ draftMode ? "Note to send with this request" : "Add message" }}</label>
          <textarea :id="composerId" class="native-textarea" maxlength="280" :value="draft"
            :disabled="isSending" @input="handleDraftInput" placeholder="Write a concise encounter note"></textarea>
          <div class="encounter-message-compose-actions">
            <span>{{ draftLength }}/280</span>
            <span class="encounter-message-bundle-note" v-if="draftMode">
              <i class="material-icons">link</i>Sent with the authorization request
            </span>
            <button type="button" class="encounter-message-send" :disabled="!canSend || isSending"
              @click.prevent="handleSend" v-else>
              <i class="material-icons">send</i>
              <span>{{ isSending ? "Sending" : "Send" }}</span>
            </button>
          </div>
        </div>

        <div class="encounter-message-readonly" v-else>
          <i class="material-icons">lock</i>
          <span>This conversation is read-only.</span>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import getItem from "@/composables/getItem";
import postItem from "@/composables/postItem";
import {
  markEncounterMessagesRead,
  unreadEncounterMessageIds,
} from "@/composables/encounterMessageReadState";

export default {
  name: "EncounterMessagesComponent",
  props: {
    encounterId: [String, Number],
    initialMessages: {
      type: Array,
      default: () => [],
    },
    draft: {
      type: String,
      default: "",
    },
    draftMode: {
      type: Boolean,
      default: false,
    },
    initiallyExpanded: {
      type: Boolean,
      default: true,
    },
    expanded: {
      type: Boolean,
      default: undefined,
    },
    showBodyClose: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    loadFromServer: {
      type: Boolean,
      default: true,
    },
    trackReadState: {
      type: Boolean,
      default: true,
    },
    emptyMessage: {
      type: String,
      default: "No messages have been added to this encounter.",
    },
  },
  emits: ["update:draft", "sent", "toggle"],
  setup(props, context) {
    const store = useStore();
    const messages = ref([...props.initialMessages]);
    const canPost = ref(false);
    const isLoading = ref(false);
    const isSending = ref(false);
    const error = ref(null);
    const messageList = ref(null);
    const composerId = `encounter-message-${Math.random().toString(36).slice(2, 10)}`;
    const panelId = `${composerId}-panel`;
    const localExpanded = ref(props.initiallyExpanded);
    const newlyViewedMessageIds = ref([]);
    let refreshRequestId = 0;
    let messageMutationVersion = 0;

    const draftLength = computed(() => Array.from(props.draft || "").length);
    const canSend = computed(() => !props.readOnly && Boolean(props.draft.trim()) && draftLength.value <= 280 && props.encounterId);
    const isControlled = computed(() => typeof props.expanded === "boolean");
    const isExpanded = computed(() => isControlled.value ? props.expanded : localExpanded.value);

    const captureUnreadMessages = () => {
      if (!props.trackReadState || !isExpanded.value || !props.encounterId || !messages.value.length) return;

      const loadedMessageIds = new Set(messages.value.map(message => String(message?.id || "")));
      const unreadIds = unreadEncounterMessageIds(store.state.user, props.encounterId)
        .filter(messageId => loadedMessageIds.has(String(messageId)));

      if (!unreadIds.length) return;

      newlyViewedMessageIds.value = Array.from(new Set([
        ...newlyViewedMessageIds.value,
        ...unreadIds.map(String),
      ]));
      markEncounterMessagesRead(store.state.user, props.encounterId, unreadIds);
    };

    const isNewMessage = message => {
      return newlyViewedMessageIds.value.includes(String(message?.id || ""));
    };

    const scrollMessagesToEnd = async () => {
      await nextTick();

      if (messageList.value) {
        messageList.value.scrollTop = messageList.value.scrollHeight;
      }
    };

    const applyPayload = payload => {
      messages.value = Array.isArray(payload?.messages) ? payload.messages : [];
      canPost.value = !props.readOnly && Boolean(payload?.can_post);
      captureUnreadMessages();
      scrollMessagesToEnd();
    };

    const refreshFromServer = async () => {
      if (!props.loadFromServer) {
        messages.value = [...props.initialMessages];
        canPost.value = false;
        isLoading.value = false;
        return true;
      }

      if (!props.encounterId) {
        messages.value = [...props.initialMessages];
        canPost.value = false;
        isLoading.value = false;
        return false;
      }

      isLoading.value = true;
      error.value = null;
      const requestId = ++refreshRequestId;
      const mutationVersion = messageMutationVersion;
      const { res, err } = await getItem("encounter", `${props.encounterId}/messages`);

      if (requestId !== refreshRequestId || mutationVersion !== messageMutationVersion) {
        return Boolean(res.value);
      }

      if (res.value) {
        applyPayload(res.value.data);
      } else {
        error.value = err.value || "Unable to load encounter messages.";
      }

      isLoading.value = false;
      return Boolean(res.value);
    };

    const newClientMessageId = () => {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }

      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
        const random = Math.random() * 16 | 0;
        const value = character === "x" ? random : (random & 0x3 | 0x8);
        return value.toString(16);
      });
    };

    const handleSend = async () => {
      if (!canSend.value || isSending.value || props.draftMode || props.readOnly) return;

      isSending.value = true;
      error.value = null;
      const { res, err } = await postItem(`encounter/${props.encounterId}/messages`, {
        body: props.draft.trim(),
        client_message_id: newClientMessageId(),
      });

      if (res.value) {
        if (String(props.encounterId) === String(res.value.data?.encounter_id)) {
          messageMutationVersion += 1;
          refreshRequestId += 1;
          isLoading.value = false;
          applyPayload(res.value.data);
          context.emit("sent", res.value.data);
        }

        context.emit("update:draft", "");
      } else {
        error.value = err.value || "Unable to send this message.";
      }

      isSending.value = false;
    };

    const handleDraftInput = event => {
      context.emit("update:draft", event.target.value);
    };

    const toggleExpanded = () => {
      const nextExpanded = !isExpanded.value;

      if (!isControlled.value) {
        localExpanded.value = nextExpanded;
      }

      context.emit("toggle", nextExpanded);

      if (nextExpanded) {
        scrollMessagesToEnd();
      }
    };

    const isOwnMessage = message => Number(message?.sender_user_id) === Number(store.state.user?.id);

    const messageSender = message => {
      if (isOwnMessage(message)) return "You";
      return message?.sender_type === "provider" ? "Provider" : "HMO";
    };

    const formatDate = value => {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) return "";

      return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    watch(() => props.encounterId, () => {
      refreshRequestId += 1;
      messageMutationVersion += 1;
      newlyViewedMessageIds.value = [];
      messages.value = Array.isArray(props.initialMessages) ? [...props.initialMessages] : [];
      refreshFromServer();
    });
    watch(() => props.initiallyExpanded, value => {
      if (!isControlled.value) {
        localExpanded.value = value;
      }
    });
    watch(() => props.initialMessages, value => {
      messages.value = Array.isArray(value) ? [...value] : [];
      captureUnreadMessages();
      scrollMessagesToEnd();
    });
    watch(isExpanded, value => {
      if (value) {
        captureUnreadMessages();
        scrollMessagesToEnd();
      }
    });

    onMounted(refreshFromServer);
    context.expose({ refreshFromServer });

    return {
      messages,
      canPost,
      isLoading,
      isSending,
      error,
      messageList,
      composerId,
      panelId,
      isExpanded,
      draftLength,
      canSend,
      handleSend,
      handleDraftInput,
      toggleExpanded,
      isOwnMessage,
      isNewMessage,
      messageSender,
      formatDate,
      refreshFromServer,
    };
  },
};
</script>

<style scoped>
.encounter-message-thread {
  background: #fff;
  border: 1px solid #c9d7dc;
  margin: 20px 0;
  position: relative;
}

.encounter-message-head {
  border-bottom: 1px solid #dce6e9;
}

.encounter-message-toggle {
  align-items: center;
  background: #fff;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 14px 18px;
  text-align: left;
  width: 100%;
}

.encounter-message-toggle:hover,
.encounter-message-toggle:focus-visible {
  background: #f8fbfb;
}

.encounter-message-toggle:focus-visible {
  outline: 2px solid #009688;
  outline-offset: -2px;
}

.encounter-message-title {
  align-items: center;
  display: flex;
  gap: 12px;
}

.encounter-message-title > div {
  display: grid;
  gap: 2px;
}

.encounter-message-title span:not(.encounter-message-icon) {
  color: #263238;
  font-weight: 700;
}

.encounter-message-title small {
  color: #607d8b;
}

.encounter-message-icon {
  align-items: center;
  background: #e8f5f3;
  color: #00897b;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.encounter-message-count {
  align-items: center;
  background: #eef3f5;
  color: #455a64;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  min-width: 28px;
  padding: 0 8px;
}

.encounter-message-controls {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 10px;
}

.encounter-message-chevron {
  color: #607d8b;
  font-size: 24px;
  transition: color 0.18s ease;
}

.encounter-message-toggle:hover .encounter-message-chevron {
  color: #00897b;
}

.encounter-message-body {
  max-height: 720px;
  overflow: hidden;
  position: relative;
}

.encounter-message-body-close {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c9d7dc;
  border-radius: 4px;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 18px;
  top: 14px;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  width: 32px;
  z-index: 2;
}

.encounter-message-body-close:hover,
.encounter-message-body-close:focus-visible {
  background: #ffebee;
  border-color: #ef9a9a;
  color: #c62828;
  outline: 0;
}

.encounter-message-body-close i {
  font-size: 19px;
}

.encounter-message-body.has-body-close .encounter-message-list,
.encounter-message-body.has-body-close .encounter-message-empty,
.encounter-message-body.has-body-close .encounter-message-error {
  padding-right: 64px;
}

.encounter-message-panel-enter-active,
.encounter-message-panel-leave-active {
  transition: max-height 0.24s ease, opacity 0.18s ease, transform 0.18s ease;
}

.encounter-message-panel-enter-from,
.encounter-message-panel-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-4px);
}

.progress.encounter-message-progress {
  height: 3px;
  margin: -3px 0 0;
}

.encounter-message-list {
  display: grid;
  gap: 10px;
  max-height: 320px;
  overflow-y: auto;
  padding: 16px 18px;
}

.encounter-message-row {
  background: #f5f7f8;
  border-left: 3px solid #90a4ae;
  max-width: 82%;
  padding: 10px 12px;
}

.encounter-message-row.is-mine {
  background: #edf8f6;
  border-left-color: #009688;
  justify-self: end;
}

.encounter-message-row.is-new {
  background: #fffbea;
  border-left-color: #f9a825;
}

.encounter-message-meta {
  align-items: center;
  color: #607d8b;
  display: flex;
  font-size: 0.78rem;
  gap: 12px;
  justify-content: space-between;
}

.encounter-message-meta strong {
  color: #37474f;
}

.encounter-message-time {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.encounter-message-new {
  background: #fff3cd;
  border: 1px solid #ffe082;
  border-radius: 3px;
  color: #8d5b00;
  font-size: 0.66rem;
  font-weight: 800;
  line-height: 1;
  padding: 4px 6px;
  text-transform: uppercase;
}

.encounter-message-row p {
  color: #263238;
  margin: 7px 0 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.encounter-message-empty,
.encounter-message-readonly,
.encounter-message-error {
  align-items: center;
  color: #607d8b;
  display: flex;
  gap: 9px;
  padding: 16px 18px;
}

.encounter-message-error {
  background: #fff4f4;
  color: #c62828;
}

.encounter-message-composer {
  border-top: 1px solid #dce6e9;
  padding: 14px 18px;
}

.encounter-message-composer label {
  color: #455a64;
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 5px;
}

.encounter-message-composer textarea.native-textarea {
  border: 1px solid #c9d7dc;
  box-sizing: border-box;
  margin: 0;
  min-height: 78px;
  padding: 10px 12px;
}

.encounter-message-composer textarea.native-textarea:focus {
  border: 1px solid #009688 !important;
  box-shadow: none !important;
}

.encounter-message-compose-actions {
  align-items: center;
  color: #607d8b;
  display: flex;
  justify-content: space-between;
  min-height: 40px;
  padding-top: 9px;
}

.encounter-message-bundle-note {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.encounter-message-bundle-note i {
  font-size: 18px;
}

.encounter-message-send {
  align-items: center;
  background: #00897b;
  border: 0;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  min-height: 40px;
  padding: 0 18px;
}

.encounter-message-send:disabled {
  background: #cfd8dc;
  cursor: default;
}

.encounter-message-send i {
  font-size: 19px;
}

@media (max-width: 600px) {
  .encounter-message-row {
    max-width: 94%;
  }

  .encounter-message-compose-actions {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
  }
}
</style>
